import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { LoopDocument } from "./LoopDocument.ts";

/**
 * Who made the last write to a draft: Claude through the orcaloops MCP, or a
 * person through the web app.
 *
 * @since 1.0.0
 */
export const LoopDraftWriterVia = Schema.Literal("mcp", "web");
export type LoopDraftWriterVia = Schema.Schema.Type<typeof LoopDraftWriterVia>;

/**
 * A draft's lifecycle: `open` while it is edited, then closed exactly once as
 * `published` (a version was minted) or `discarded`. Closed drafts are kept.
 *
 * @since 1.0.0
 */
export const LoopDraftStatus = Schema.Literal("open", "published", "discarded");
export type LoopDraftStatus = Schema.Schema.Type<typeof LoopDraftStatus>;

/**
 * Loop draft model — the ONE open working draft of a loop document per
 * (customer, slug), edited by whole-document writes under a compare-and-set
 * `revision`, then closed as published or discarded.
 *
 * `body` is the whole v3 loop document, structurally opaque to `@fm/db`, and
 * never null: a draft always holds a document, valid or not — findings are
 * computed by the caller, never gated here. `customerId`, `loopId`,
 * `lastWriterPersonId` and `publishedLoopId` are by id only.
 *
 * @since 1.0.0
 */
export class LoopDraft extends Model.Class<LoopDraft>("LoopDraft")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,

  /** The loop handle this draft targets. At most one OPEN draft per customer. */
  slug: Schema.String,
  /** The existing loop being edited; `null` for a new loop. */
  loopId: Schema.NullOr(Schema.String),

  name: Schema.String,
  description: Schema.NullOr(Schema.String),

  /** The whole v3 loop document, key order preserved (a `json` column). */
  body: LoopDocument,

  /** Bumped by exactly one on every write AND on close. */
  revision: Schema.Number,

  lastWriterPersonId: Schema.NullOr(Schema.String),
  lastWriterVia: LoopDraftWriterVia,

  status: LoopDraftStatus,
  /** Set when closed as `published`: the loop and version the publish minted. */
  publishedLoopId: Schema.NullOr(Schema.String),
  publishedVersion: Schema.NullOr(Schema.Number),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
